import { useState, useRef } from "react";
import { useMutation } from "@apollo/client";

import {
  REPORT_RECORDING_OPEN,
  REPORT_RECORDING_VIDEO_START,
  REPORT_RECORDING_VIDEO_PROGRESS,
  REPORT_RECORDING_VIDEO_END,
} from "utility/graphQl/mutation";

import constant from "Constant";
import { logError } from "utility/Function";
import appInfo from "utility/app-info";

const useReportRecordingEngagement = (recordingId: string) => {
  const [isOpened, setIsOpened] = useState(false);

  const groupUuidRef = useRef(null);
  // Refs, not state: the once-per-session guards must hold synchronously — a
  // setState-based guard doesn't flush before a burst of rapid events (e.g.
  // autoplay retry loops firing several `play`s) can slip past it.
  const hasSentStartRef = useRef(false);
  const hasSentEndRef = useRef(false);
  const progressReportRef = useRef({
    10: false,
    25: false,
    50: false,
    75: false,
  });
  // Every recording mutation requires the groupUuid, which only arrives with
  // ReportRecordingOpen's async response. Actions requested before it lands
  // (autoplay can fire onPlay/onEnded before the open round-trip returns) are
  // queued here and flushed in order once it resolves — sending without the
  // groupUuid is rejected by GraphQL (required ID!) before the resolver runs.
  const pendingActionsRef = useRef<Array<() => void>>([]);

  // Run now if the groupUuid is already available, else defer until it is.
  const withGroupUuid = (run: () => void) => {
    if (groupUuidRef.current) {
      run();
    } else {
      pendingActionsRef.current.push(run);
    }
  };

  const [reportRecordingOpen] = useMutation(REPORT_RECORDING_OPEN, {
    fetchPolicy: "no-cache",
    onCompleted(response) {
      groupUuidRef.current = response.reportRecordingOpen.groupUuid;
      const pending = pendingActionsRef.current;
      pendingActionsRef.current = [];
      pending.forEach((run) => run());
    },
    onError(error) {
      logError("reportRecordingOpen error", error);
    },
  });

  const [reportRecordingVideoStart] = useMutation(REPORT_RECORDING_VIDEO_START);

  const [reportRecordingVideoProgress] = useMutation(
    REPORT_RECORDING_VIDEO_PROGRESS
  );

  const [reportRecordingVideoEnd] = useMutation(REPORT_RECORDING_VIDEO_END);

  const handleOpen = () => {
    if (!isOpened) {
      reportRecordingOpen({
        variables: {
          recordingId: recordingId,
          at: new Date().getTime(),
          platform: constant.APP_PLATFORM,
          appInfo: appInfo,
        },
      });

      setIsOpened(true);
    }
  };

  const handlePlay = () => {
    if (hasSentStartRef.current) return;
    hasSentStartRef.current = true;

    // Capture the timestamp at play time, not at flush time.
    const at = new Date().getTime();
    withGroupUuid(() =>
      reportRecordingVideoStart({
        variables: {
          recordingId: recordingId,
          at: at,
          platform: constant.APP_PLATFORM,
          groupUuid: groupUuidRef.current,
          appInfo: appInfo,
        },
      })
    );
  };

  const handleEnd = () => {
    if (hasSentEndRef.current) return;
    hasSentEndRef.current = true;

    // Capture the timestamp at end time, not at flush time.
    const at = new Date().getTime();
    withGroupUuid(() =>
      reportRecordingVideoEnd({
        variables: {
          recordingId: recordingId,
          at: at,
          platform: constant.APP_PLATFORM,
          groupUuid: groupUuidRef.current,
          appInfo: appInfo,
        },
      })
    );
  };

  const handleProgress = (event) => {
    // Drop, don't defer: progress fires continuously, so a tick skipped before
    // the groupUuid lands self-heals on the next one — and deferring would
    // report a stale currentTime snapshot. (onPlay/onEnd fire once, so they
    // defer instead; see withGroupUuid.)
    if (!groupUuidRef.current) return;

    const { duration, currentTime } = event.target;
    const percentage = (currentTime / duration) * 100;
    if (!progressReportRef.current[10] && percentage >= 10 && percentage < 25) {
      reportRecordingVideoProgress({
        variables: {
          recordingId: recordingId,
          at: new Date().getTime(),
          platform: constant.APP_PLATFORM,
          groupUuid: groupUuidRef.current,
          percentage,
          duration,
          currentTime,
          appInfo: appInfo,
        },
      });
      progressReportRef.current = {
        ...progressReportRef.current,
        10: true,
      };
    } else if (
      !progressReportRef.current[25] &&
      percentage >= 25 &&
      percentage < 50
    ) {
      reportRecordingVideoProgress({
        variables: {
          recordingId: recordingId,
          at: new Date().getTime(),
          platform: constant.APP_PLATFORM,
          groupUuid: groupUuidRef.current,
          percentage,
          duration,
          currentTime,
          appInfo: appInfo,
        },
      });

      progressReportRef.current = {
        ...progressReportRef.current,
        10: true,
        25: true,
      };
    } else if (
      !progressReportRef.current[50] &&
      percentage >= 50 &&
      percentage < 75
    ) {
      reportRecordingVideoProgress({
        variables: {
          recordingId: recordingId,
          at: new Date().getTime(),
          platform: constant.APP_PLATFORM,
          groupUuid: groupUuidRef.current,
          percentage,
          duration,
          currentTime,
          appInfo: appInfo,
        },
      });

      progressReportRef.current = {
        ...progressReportRef.current,
        10: true,
        25: true,
        50: true,
      };
    } else if (
      !progressReportRef.current[75] &&
      percentage >= 75 &&
      percentage < 100
    ) {
      reportRecordingVideoProgress({
        variables: {
          recordingId: recordingId,
          at: new Date().getTime(),
          platform: constant.APP_PLATFORM,
          groupUuid: groupUuidRef.current,
          percentage,
          duration,
          currentTime,
          appInfo: appInfo,
        },
      });

      progressReportRef.current = {
        ...progressReportRef.current,
        10: true,
        25: true,
        50: true,
        75: true,
      };
    }
  };

  return {
    onOpen: handleOpen,
    onPlay: handlePlay,
    onEnd: handleEnd,
    onProgress: handleProgress,
  };
};

export default useReportRecordingEngagement;
